import React, { FC, useEffect, useState } from 'react';
import Container from '@/components/Container';
import { Typography, Button } from '@mui/material';
import { Api } from '@/services/Api';
import { Owner } from '@/declarations/models/Owner';
import { ChildrenProp } from '@/declarations/ChildrenProp';
import { ToolbarHeader } from '@/components/ToolbarHeader';
import { formatAPITimestamp } from '@/utils/dates';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import FullScreenModal from '@/components/FullScreenModal';
import { OwnerEditor } from './AdminOwnerEditor';

export const AdminOwners: FC<ChildrenProp> = () => {
  // console.log('selectedSite', selectedSite);
  const [owners, setOwners] = useState<Array<Owner>>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerId, setOwnerId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  const filteredOwners = owners?.filter((owner) => {
    const searchQueryLower = searchQuery.toLowerCase();
    return searchQueryLower.endsWith('*')
      ? owner.name.toLowerCase().startsWith(searchQueryLower.slice(0, -1)) ||
          owner?.name.toLowerCase().startsWith(searchQueryLower.slice(0, -1))
      : owner.name.toLowerCase().includes(searchQueryLower) ||
          (owner?.id !== undefined && owner?.id?.toString() === searchQueryLower) ||
          owner.identifier.toLowerCase().includes(searchQueryLower) ||
          (owner.parent && owner.parent.name && owner?.parent?.name.toLowerCase().includes(searchQueryLower)) ||
          (owner.parent_identifier && owner?.parent_identifier.toLowerCase().includes(searchQueryLower));
  });

  const totalSites = owners?.reduce((acc, owner) => acc + (owner.sites?.length || 0), 0);
  const filteredSites = filteredOwners?.reduce((acc, owner) => acc + (owner.sites?.length || 0), 0);

  const onAddNewOwner = () => {
    // console.log('open owner modal');
    setOwnerId(null);
    setEditModalOpen(true);
  };

  const handleOnItemClick = (item: Partial<Owner> | undefined) => {
    if (item && item.id) {
      setOwnerId(item.id);
      setEditModalOpen(true);
    }
  };

  const [refreshDep, setRefreshDep] = useState(Date.now);
  const refreshPage = () => setRefreshDep(Date.now());

  const handleCloseModal = () => {
    refreshPage();
    setEditModalOpen(false);
  };

  useEffect(() => {
    Api.getOwners({ rows: 500 })
      .fetch()
      .then((data) => {
        setOwners(data[0]?.owners || []);
        // console.log(data[0]?.owners);
      });
  }, [refreshDep]);

  return (
    <Container left fullWidth p={2} top mt={0} column>
      <FullScreenModal modalOpen={editModalOpen}>
        <Container fullWidth left>
          <OwnerEditor ownerId={ownerId} onCloseEditor={handleCloseModal} />
        </Container>
      </FullScreenModal>

      <ToolbarHeader
        heading={
          <Typography variant='h3' sx={{ component: 'h1', fontSize: 30 }}>
            Museer ({owners?.length}) og nettsteder ({totalSites})
          </Typography>
        }
        topRight={
          <Button
            onClick={onAddNewOwner}
            variant='contained'
            color='success'
            startIcon={<MaterialSymbol name='add' />}
            type='button'>
            Nytt Museum (eier)
          </Button>
        }
        query={searchQuery}
        onQueryChange={setSearchQuery}
      />

      <Container left fullWidth pt={3} top mt={0} column sx={{ height: '75vh' }}>
        <TableContainer component={Paper}>
          <Table className='compact-table' stickyHeader sx={{ minWidth: 650 }}>
            <TableHead className='table-head'>
              <TableRow>
                <TableCell>MUSEUM ({filteredOwners?.length})</TableCell>
                <TableCell align='center'>M24-ID</TableCell>
                <TableCell align='center'>Opprettet/Endret</TableCell>
                <TableCell align='center'>Museumssignatur</TableCell>
                <TableCell align='center'>Ekultur Museum ID</TableCell>
                <TableCell align='center'>Nettsteder ({filteredSites})</TableCell>
                <TableCell align='center'>Antall undermuseer</TableCell>
                <TableCell>En del av</TableCell>
                <TableCell align='center'>DAMS aktivert</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOwners
                ?.sort((a, b) => {
                  const aName = a.name.toLowerCase();
                  const bName = b.name.toLowerCase();
                  if (aName.startsWith('æ') || aName.startsWith('ø') || aName.startsWith('å')) {
                    return 1;
                  }
                  if (bName.startsWith('æ') || bName.startsWith('ø') || bName.startsWith('å')) {
                    return -1;
                  }

                  return aName.localeCompare(bName);
                })
                ?.map((child) => (
                  <TableRow key={child.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell scope='row'>
                      <Button
                        style={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                        onClick={() => handleOnItemClick(child)}>
                        {child.name}
                      </Button>
                    </TableCell>
                    <TableCell align='center'>{child.id}</TableCell>
                    <TableCell align='center'>
                      {formatAPITimestamp(child.created_at)} / {formatAPITimestamp(child.updated_at)}
                    </TableCell>
                    <TableCell align='center'>{child.identifier}</TableCell>
                    <TableCell align='center'>{child.ekultur_museum_id}</TableCell>
                    <TableCell align='center'>{child.sites?.length || 0}</TableCell>
                    <TableCell align='center'>{child.children?.length || 0}</TableCell>
                    <TableCell>
                      {child?.parent?.name ? (
                        <Button
                          style={{ cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}
                          onClick={() => handleOnItemClick(child?.parent)}>
                          {child?.parent?.name}
                        </Button>
                      ) : (
                        ' - TOPLEVEL - '
                      )}
                    </TableCell>
                    <TableCell align='center'>{child.dams_integration_enabled ? '✅' : '❌'}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Container>
  );
};

export default AdminOwners;
